import React, { FC, SyntheticEvent, HTMLAttributes } from 'react';
import { Box, TextField, CircularProgress } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { GooglePlacesAutocompleteProps } from './types';
import { useGooglePlacesPredictions } from '../../../hooks/useGooglePlacesPredictions';

export const GooglePlacesAutocomplete: FC<GooglePlacesAutocompleteProps> = ({
  disabled = false,
  fullWidth = true,
  onKeyDown,
  onPlaceSelect,
  onValueChange,
  placeholder = 'Search for a place...',
  size = 'small',
  value,
  textFieldSx
}) => {
  const {
    predictions,
    loading: loadingPredictions,
    scriptLoaded,
    fetchPredictions
  } = useGooglePlacesPredictions();

  const handleInputChange = (_: SyntheticEvent, newInputValue: string) => {
    onValueChange(newInputValue);

    if (!newInputValue.trim()) {
      return;
    }

    fetchPredictions(newInputValue);
  };

  const handleOptionSelect = (
    _: SyntheticEvent,
    option: string | google.maps.places.AutocompletePrediction | null
  ) => {
    if (option && typeof option === 'object' && onPlaceSelect) {
      onPlaceSelect(option);
    }
  };

  const renderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: google.maps.places.AutocompletePrediction
  ) => (
    <Box component="li" {...props}>
      <Box>
        <Box sx={{ fontWeight: 'bold' }}>
          {option.structured_formatting?.main_text || option.description}
        </Box>
        {option.structured_formatting?.secondary_text && (
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {option.structured_formatting.secondary_text}
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Autocomplete
      size={size}
      freeSolo
      options={predictions}
      value={value}
      onChange={handleOptionSelect}
      onInputChange={handleInputChange}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.description || '';
      }}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          disabled={disabled || !scriptLoaded}
          onKeyDown={onKeyDown}
          sx={textFieldSx}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingPredictions && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
      loading={loadingPredictions}
      fullWidth={fullWidth}
      disabled={disabled || !scriptLoaded}
    />
  );
};
